import React, { useState, useEffect } from 'react';
import { Building2, Check, X } from 'lucide-react';
import { supabase } from '../config/supabase';

interface Empresa {
  id: string;
  nome: string;
  cnpj: string;
  razao_social?: string;
  numero_funcionarios?: number;
  grau_risco?: string;
  status?: string;
  tipo_acesso?: string;
  ativo?: boolean;
  data_termino_mandato?: string;
}

interface EmpresaSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEmpresa: (empresa: Empresa) => void;
  usuarioId: string;
}

const EmpresaSelectorModal: React.FC<EmpresaSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelectEmpresa,
  usuarioId
}) => {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmpresa, setSelectedEmpresa] = useState<Empresa | null>(null);

  // Carregar empresas do usuário
  useEffect(() => {
    if (isOpen && usuarioId) {
      carregarEmpresas();
    }
  }, [isOpen, usuarioId]);

  const carregarEmpresas = async () => {
    try {
      setLoading(true);
      
      // Buscar empresas ativas do usuário
      const { data: empresasData, error } = await supabase
        .from('cp_usuario_empresas')
        .select(`
          empresa:cp_empresas(
            id,
            nome,
            cnpj,
            razao_social,
            numero_funcionarios,
            grau_risco,
            status,
            cnae_principal,
            tipo_comissao
          )
        `)
        .eq('usuario_id', usuarioId)
        .eq('ativo', true)
        .eq('empresa.status', 'ATIVA');

      if (error) {
        console.error('Erro ao carregar empresas:', error);
        return;
      }

      const empresasList = empresasData
        ?.map(item => item.empresa)
        .filter(Boolean) as unknown as Empresa[];

      setEmpresas(empresasList || []);
      
      // Se tiver apenas uma empresa, selecionar automaticamente
      if (empresasList && empresasList.length === 1) {
        setSelectedEmpresa(empresasList[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmpresa = () => {
    if (selectedEmpresa) {
      onSelectEmpresa(selectedEmpresa);
      onClose();
    }
  };

  const getGrauRiscoInfo = (grau?: string) => {
    const info = {
      '1': { label: 'Baixo', color: 'text-green-600' },
      '2': { label: 'Médio', color: 'text-yellow-600' },
      '3': { label: 'Alto', color: 'text-orange-600' },
      '4': { label: 'Muito Alto', color: 'text-red-600' }
    };
    return info[grau as keyof typeof info] || info['2'];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Selecionar Empresa</h3>
              <p className="text-sm text-gray-600">Escolha a empresa para continuar</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando empresas...</p>
          </div>
        )}

        {/* Lista de Empresas */}
        {!loading && empresas.length > 0 && (
          <div className="space-y-3">
            {empresas.map((empresa) => {
              const grauInfo = getGrauRiscoInfo(empresa.grau_risco);
              const isSelected = selectedEmpresa?.id === empresa.id;
              
              return (
                <div
                  key={empresa.id}
                  onClick={() => setSelectedEmpresa(empresa)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {empresa.nome}
                        </h4>
                        {isSelected && (
                          <div className="p-1 bg-blue-500 rounded-full">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">CNPJ:</span> {empresa.cnpj}
                        </div>
                        <div>
                          <span className="font-medium">Funcionários:</span> {empresa.numero_funcionarios}
                        </div>
                        <div>
                          <span className="font-medium">Razão Social:</span> {empresa.razao_social}
                        </div>
                        <div>
                          <span className="font-medium">Grau de Risco:</span>
                          <span className={`ml-1 font-semibold ${grauInfo.color}`}>
                            {grauInfo.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Nenhuma empresa encontrada */}
        {!loading && empresas.length === 0 && (
          <div className="text-center py-8">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Nenhuma empresa encontrada</h4>
            <p className="text-gray-600">Você não possui empresas cadastradas.</p>
          </div>
        )}

        {/* Botões */}
        {!loading && empresas.length > 0 && (
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSelectEmpresa}
              disabled={!selectedEmpresa}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedEmpresa
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continuar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmpresaSelectorModal;
